import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Alert, Box, Button, Snackbar, Stack, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import MainLayout from '../../components/MainLayout';
import TransferLeadDialog from '../../components/TransferLeadDialog';
import {
  useLeadsQuery,
  useAcceptLead,
  useReleaseLead,
  useCloseLead,
  useReopenLead,
  usePinLead,
  useUnpinLead,
  useReorderPins,
  useFavoriteLead,
  useUnfavoriteLead,
  useLeadListsQuery,
} from './hooks/useLeadsQuery';
import { transformConversation } from './utils/leadTransform';
import { useDepartments } from '../../hooks/useDepartments';
import {
  buildFilterModel,
  buildSortModel,
  countActiveFilters,
  readStoredDetailed,
  readStoredFilters,
  readStoredSort,
  selectChipFilters,
  writeStoredDetailed,
  writeStoredFilters,
  writeStoredSort,
} from './utils/leadFilters';
import LeadsList from './components/Leads.List';
import LeadsQueueModal from './components/Leads.QueueModal';
import LeadsFilterPanel from './components/Leads.FilterPanel';
import LeadsFilterChips from './components/Leads.FilterChips';
import LeadsSortSelect from './components/Leads.SortSelect';
import LeadsListChips from './components/Leads.ListChips';
import LeadsListDialog from './components/Leads.ListDialog';
import LeadsListsManagerDialog from './components/Leads.ListsManagerDialog';
import LeadsAddToListDialog from './components/Leads.AddToListDialog';
import LeadsTopBarMenu from './components/Leads.TopBarMenu';

const Leads = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const queryClient = useQueryClient();

  // Filtros, ordenação e busca controlam o queryKey → refetch automático
  const [filters, setFilters] = useState(readStoredFilters);
  const [collaborators, setCollaborators] = useState([]);
  const [sort, setSort] = useState(readStoredSort);
  const [detailed, setDetailed] = useState(readStoredDetailed);
  const [search, setSearch] = useState('');
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedLeadForTransfer, setSelectedLeadForTransfer] = useState(null);
  // null = fechado; {} = criar; objeto da lista = editar
  const [listDialog, setListDialog] = useState(null);
  const [listsManagerOpen, setListsManagerOpen] = useState(false);
  const [leadForLists, setLeadForLists] = useState(null);
  // Snackbar único da página: { message, severity } ou null.
  const [notice, setNotice] = useState(null);

  // Estado de ação pendente por lead (uuid → 'accept' | 'release' | 'close' | 'reopen')
  const [pendingActions, setPendingActions] = useState({});

  useEffect(() => {
    writeStoredFilters(filters);
  }, [filters]);

  useEffect(() => {
    writeStoredSort(sort);
  }, [sort]);

  useEffect(() => {
    writeStoredDetailed(detailed);
  }, [detailed]);

  const queryOptions = useMemo(
    () => ({ filterModel: buildFilterModel(filters), sortModel: buildSortModel(sort), search }),
    [filters, sort, search],
  );

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);
  const { data: leadLists = [] } = useLeadListsQuery();
  const selectedList = leadLists.find((list) => list.uuid === filters.list);

  // TanStack Query — infinite scroll + polling a cada 2s
  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useLeadsQuery(queryOptions);

  const reloadLead = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['leads', queryOptions] });
  }, [queryClient, queryOptions]);

  const { data: departments = [] } = useDepartments();

  const allLeads = data?.pages.flatMap(
    (page) => page.results.map((conv) => transformConversation(conv, departments)),
  ) ?? [];

  // Mutations
  const acceptMutation = useAcceptLead();
  const releaseMutation = useReleaseLead();
  const closeMutation = useCloseLead();
  const reopenMutation = useReopenLead();
  const pinMutation = usePinLead();
  const unpinMutation = useUnpinLead();
  const reorderPinsMutation = useReorderPins();
  const favoriteMutation = useFavoriteLead();
  const unfavoriteMutation = useUnfavoriteLead();

  const setPending = useCallback((uuid, action) => {
    setPendingActions((prev) => ({ ...prev, [uuid]: action }));
  }, []);

  useEffect(() => {
    setPendingActions({});
  }, [data]);

  const handleSearch = useCallback((value) => {
    setSearch(value);
  }, []);

  const handleLeadDetails = (lead) => {
    if (!lead.contactUuid) return;
    navigate(
      isDesktop ? `/clients/${lead.contactUuid}` : `/customer-details/${lead.contactUuid}`,
      { state: { from: location.pathname } }
    );
  };

  const handleAcceptLead = (lead) => {
    setPending(lead.uuid, 'accept');
    acceptMutation.mutate(lead.uuid, {
      onSuccess: () => {
        navigate(`/leads/${lead.uuid}`);
      },
      onError: () => reloadLead(),
    });
  };

  const handleReleaseLead = (lead) => {
    setPending(lead.uuid, 'release');
    releaseMutation.mutate(lead.uuid, {
      onSettled: () => reloadLead(),
    });
  };

  const handleCloseLead = (lead) => {
    setPending(lead.uuid, 'close');
    closeMutation.mutate(lead.uuid, {
      onSettled: () => reloadLead(),
    });
  };

  const handleReopenLead = (lead) => {
    setPending(lead.uuid, 'reopen');
    reopenMutation.mutate(lead.uuid, {
      onSettled: () => reloadLead(),
    });
  };

  const handlePinLead = (lead) => {
    setPending(lead.uuid, 'pin');
    pinMutation.mutate(lead.uuid, {
      // O 400 do limite de pins é a única resposta de erro que o colaborador
      // precisa ler — as outras já se resolvem no refetch.
      onError: (error) => {
        const limit = error?.response?.data?.limit;
        setNotice({
          severity: 'error',
          message: limit ? t('leads.pinLimitReached', { count: limit }) : t('error'),
        });
      },
      onSuccess: () => setNotice({ severity: 'success', message: t('leads.pinned') }),
      onSettled: () => reloadLead(),
    });
  };

  const handleUnpinLead = (lead) => {
    setPending(lead.uuid, 'unpin');
    unpinMutation.mutate(lead.uuid, {
      onSuccess: () => setNotice({ severity: 'success', message: t('leads.unpinned') }),
      onSettled: () => reloadLead(),
    });
  };

  // Devolve a promise: Leads.PinnedGroup segura a ordem local até o refetch
  // confirmar, e desfaz se o PUT falhar.
  const handleReorderPins = (uuids) =>
    reorderPinsMutation.mutateAsync(uuids).finally(reloadLead);

  const handleFavoriteLead = (lead) => {
    setPending(lead.uuid, 'favorite');
    favoriteMutation.mutate(lead.uuid, {
      onSuccess: () => setNotice({ severity: 'success', message: t('leads.favorited') }),
      onSettled: () => reloadLead(),
    });
  };

  const handleUnfavoriteLead = (lead) => {
    setPending(lead.uuid, 'unfavorite');
    unfavoriteMutation.mutate(lead.uuid, {
      onSuccess: () => setNotice({ severity: 'success', message: t('leads.unfavorited') }),
      onSettled: () => reloadLead(),
    });
  };

  const handleTransferLead = (lead) => {
    setSelectedLeadForTransfer(lead);
    setTransferDialogOpen(true);
  };

  // Abre a conversa do lead: chat ao vivo se o colaborador pode responder,
  // senão a tela de preview read-only (lead não aceito / de terceiros / histórico).
  const handleOpenConversation = (lead) => {
    if (lead.canSendMessages) {
      navigate(`/leads/${lead.uuid}`);
    } else {
      navigate(`/leads/${lead.uuid}/preview`);
    }
  };

  const handleTransferSuccess = () => {
    reloadLead();
  };

  return (
    <MainLayout
      title={t('leads.title')}
      showFilter={true}
      onFilterClick={() => setFilterPanelOpen(true)}
      activeFilterCount={activeFilterCount}
      topBarActions={(
        <LeadsTopBarMenu
          detailed={detailed}
          onToggleDetailed={() => setDetailed((prev) => !prev)}
          onOpenLists={() => setListsManagerOpen(true)}
        />
      )}
      showSearch={!filterPanelOpen}
      onSearch={handleSearch}
      searchPlaceholder={t('leads.searchLead')}
      searchFooter={(
        <LeadsListChips
          filters={filters}
          onChange={setFilters}
          onCreate={() => setListDialog({})}
          onEdit={setListDialog}
        />
      )}
    >
      <Box
        sx={(theme) => ({
          pb: 2,
          minHeight: '100vh',
          [theme.breakpoints.up('md')]: {
            minHeight: 'unset',
          },
        })}
      >
        <Box sx={{ px: 2, pt: 2 }}>
          <LeadsFilterChips filters={filters} onChange={setFilters} collaborators={collaborators} />
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1}>
            {/* Como o "Gerenciar" do WhatsApp, mas acima dos cards: abaixo deles ficaria fora da tela. */}
            {selectedList ? (
              <Button
                size="small"
                onClick={() => setListDialog(selectedList)}
                sx={{ minWidth: 0, textTransform: 'none' }}
              >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t('leads.lists.manageNamed', { name: selectedList.name })}
                </Box>
              </Button>
            ) : <Box />}
            <LeadsSortSelect value={sort} onChange={setSort} />
          </Stack>
          <LeadsList
            leads={allLeads}
            isLoading={isLoading}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onFetchNextPage={fetchNextPage}
            pendingActions={pendingActions}
            onAccept={handleAcceptLead}
            onRelease={handleReleaseLead}
            onClose={handleCloseLead}
            onReopen={handleReopenLead}
            onTransfer={handleTransferLead}
            onLeadDetails={handleLeadDetails}
            onOpenConversation={handleOpenConversation}
            onPin={handlePinLead}
            onUnpin={handleUnpinLead}
            onReorderPins={handleReorderPins}
            onFavorite={handleFavoriteLead}
            onUnfavorite={handleUnfavoriteLead}
            onManageLists={setLeadForLists}
            detailed={detailed}
          />
        </Box>
      </Box>

      <LeadsFilterPanel
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        filters={filters}
        onApply={(draft, collabs) => {
          setFilters(draft);
          if (Array.isArray(collabs)) setCollaborators(collabs);
        }}
      />

      <LeadsQueueModal
        open={queueModalOpen}
        onClose={() => setQueueModalOpen(false)}
      />

      <TransferLeadDialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        lead={selectedLeadForTransfer}
        onTransferSuccess={handleTransferSuccess}
      />

      {/* Antes do LeadsListDialog: criar/gerenciar abre por cima da tela de listas. */}
      <LeadsListsManagerDialog
        open={listsManagerOpen}
        onClose={() => setListsManagerOpen(false)}
        onCreate={() => setListDialog({})}
        onManage={setListDialog}
      />

      {listDialog && (
        <LeadsListDialog
          list={listDialog}
          onClose={() => setListDialog(null)}
          onCreated={(created) => {
            setListDialog(null);
            setFilters((prev) => selectChipFilters(prev, { list: created.uuid }));
          }}
        />
      )}

      {leadForLists && (
        <LeadsAddToListDialog lead={leadForLists} onClose={() => setLeadForLists(null)} />
      )}
      <Snackbar
        key={notice?.message}
        open={Boolean(notice)}
        autoHideDuration={notice?.severity === 'success' ? 2000 : 4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notice?.severity} variant="filled" onClose={() => setNotice(null)}>
          {notice?.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default Leads;
